import { journeyStrings } from "@/journey/strings";
import { selectedOfferings, type ExecutableDiscoveryModel } from "@/domain/discovery-model";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";
import {
  isNumericPricing,
  type DiscoveryAnswers,
  type DiscoveryEstimate,
  type PricingFactor,
} from "@/domain/types";
import { evaluatePricedSubtotal, matchesAnswer } from "./engine";
import { pricingRules, type PricingRule } from "./pricing-edit";

export type PricePresentation = {
  origin: "source" | "derived" | "pending" | "hidden" | "quote-required";
  estimate: DiscoveryEstimate | null;
  display: "range" | "from" | "fixed";
  source?: PricingRule | undefined;
  rules: PricingRule[];
  unresolved: string[];
};
const bindings = (f: PricingFactor) => [
  { id: f.answerId, values: f.answerValues },
  { id: f.conditionAnswerId, values: f.conditionAnswerValues },
];
function couldApply(f: PricingFactor, answers: DiscoveryAnswers) {
  return bindings(f).every(
    ({ id, values }) =>
      !id ||
      answers[id] === undefined ||
      !values?.length ||
      values.some((v) =>
        Array.isArray(answers[id])
          ? (answers[id] as string[]).includes(String(v))
          : answers[id] === v,
      ),
  );
}

/** Enumerate finite executable alternatives, then use the existing engine for
 * each price. Never invent quantities or change the persisted model. Optional,
 * multi-choice and numeric unknowns remain pending, as do oversized previews. */
export function projectPricing(
  model: ExecutableDiscoveryModel,
  input: DiscoveryAnswers,
): PricePresentation {
  const initial = resolveDiscoveryPath(model, input).answers;
  const allRules = pricingRules(model);
  const rules = new Map<string, PricingRule>();
  const unresolved = new Set<string>();
  const estimates: DiscoveryEstimate[] = [];
  let pending = false,
    hidden = model.scenario.pricingDisplay === "hidden",
    quote = false;
  let starting = model.scenario.pricingDisplay === "from",
    fixed = true,
    visits = 0;
  function visit(input: DiscoveryAnswers) {
    if (++visits > 64) {
      pending = true;
      return;
    }
    const route = resolveDiscoveryPath(model, input);
    const answers = route.answers;
    const selected = selectedOfferings(model, answers);
    if (selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "hidden")) {
      hidden = true;
      return;
    }
    const possible = selected
      .flatMap((o) => o.pricing.factors)
      .filter((f) => couldApply(f, answers));
    const refs = new Set(possible.flatMap((f) => bindings(f).flatMap((b) => (b.id ? [b.id] : []))));
    // Branching choices may activate pricing dependencies on another path.
    const choice = route.path
      .map((p) => p.question)
      .find(
        (q) =>
          answers[q.id] === undefined &&
          (refs.has(q.id) ||
            (refs.size > 0 &&
              Object.keys(model.navigation?.routes[q.id]?.answers ?? {}).length > 0)) &&
          (q.kind === "cards" || q.kind === "chips"),
      );
    if (choice && "options" in choice) {
      unresolved.add(choice.id);
      if (choice.required === false) {
        pending = true;
        return;
      }
      for (const option of choice.options) visit({ ...answers, [choice.id]: option.id });
      return;
    }
    const missing = possible.flatMap((f) =>
      bindings(f).flatMap(({ id }) =>
        id && answers[id] === undefined && route.relevantQuestionIds.has(id) ? [id] : [],
      ),
    );
    if (missing.length) {
      missing.forEach((id) => unresolved.add(id));
      pending = true;
      return;
    }
    if (!selected.some((o) => isNumericPricing(o.pricing))) {
      quote = true;
      return;
    }
    // A zero base on a quantity-priced offering isn't a free project when no
    // executable monetary rate covers the chosen variant.
    if (
      selected.some(
        (o) =>
          isNumericPricing(o.pricing) &&
          o.pricing.low === 0 &&
          o.pricing.high === 0 &&
          !(o.pricing.display === "fixed" && o.pricing.fixed) &&
          o.pricing.factors.some((f) => f.kind === "per-unit") &&
          !o.pricing.factors.some((f) => f.kind !== "multiplier" && matchesAnswer(answers, f)),
      )
    ) {
      pending = true;
      return;
    }
    for (const rule of allRules) {
      if (!selected.includes(rule.offering)) continue;
      if (rule.factor && !matchesAnswer(answers, rule.factor)) continue;
      rules.set(rule.key, rule);
    }
    starting ||= selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "from");
    fixed &&= selected
      .filter((o) => isNumericPricing(o.pricing))
      .every((o) => o.pricing.display === "fixed");
    const estimate = evaluatePricedSubtotal(model, answers);
    if (estimate) estimates.push(estimate);
    else quote = true;
  }
  if (!hidden) visit(initial);
  const estimate =
    !hidden && !pending && !quote && estimates.length
      ? {
          ...estimates[0]!,
          low: Math.min(...estimates.map((e) => e.low)),
          high: Math.max(...estimates.map((e) => e.high)),
        }
      : null;
  return {
    origin: hidden ? "hidden" : pending ? "pending" : quote ? "quote-required" : "derived",
    estimate,
    display: starting
      ? "from"
      : estimate?.low === estimate?.high && (fixed || model.scenario.pricingDisplay === "fixed")
        ? "fixed"
        : "range",
    rules: hidden ? [] : [...rules.values()],
    unresolved: [...unresolved],
  };
}

/** Preview one offering through the engine, keeping candidate-answer navigation. */
export function offeringPricing(
  model: ExecutableDiscoveryModel,
  answers: DiscoveryAnswers,
  collection: "products" | "services",
  offeringId: string,
): PricePresentation {
  const scoped: ExecutableDiscoveryModel = {
    ...model,
    selection: {
      product: { kind: "fixed", offeringId: collection === "products" ? offeringId : null },
      service: { kind: "fixed", offeringId: collection === "services" ? offeringId : null },
    },
  };
  const presentation = projectPricing(scoped, answers);
  const offering = model[collection].find((o) => o.id === offeringId)!;
  if (
    presentation.estimate &&
    isNumericPricing(offering.pricing) &&
    !offering.pricing.factors.length
  ) {
    presentation.origin = "source";
    presentation.source = pricingRules(model).find(
      (r) => r.collection === collection && r.offering.id === offeringId && r.kind === "base",
    );
  }
  return presentation;
}

/** An answer source needs one selected owner and an explicit option binding.
 * The quantity is distinct from a conditional material/type answer. */
export function answerPriceSource(
  model: ExecutableDiscoveryModel,
  input: DiscoveryAnswers,
  questionId: string,
  optionId?: string,
): PricingRule | undefined {
  if (model.scenario.pricingDisplay === "hidden") return;
  const route = resolveDiscoveryPath(model, input);
  const answers = route.answers;
  const relevantFactor = (f: PricingFactor) =>
    bindings(f).every((b) => !b.id || route.questionIds.has(b.id));
  const selected = selectedOfferings(model, answers);
  if (selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "hidden")) return;
  const candidates = pricingRules(model).filter((rule) => {
    if (!selected.includes(rule.offering) || rule.kind === "base") return false;
    const f = rule.factor!;
    const local = bindings(f).some(
      ({ id, values }) =>
        id === questionId &&
        (optionId === undefined ? !values?.length : values?.length === 1 && values[0] === optionId),
    );
    if (!local || !relevantFactor(f) || !couldApply(f, answers)) return false;
    return bindings(f).every(
      ({ id }) =>
        !id ||
        id === questionId ||
        (f.kind === "per-unit" && id === f.answerId && !f.answerValues?.length) ||
        answers[id] !== undefined,
    );
  });
  if (candidates.length !== 1) return;
  const rule = candidates[0]!,
    f = rule.factor!;
  const conflicts = selected
    .flatMap((o) => o.pricing.factors)
    .some(
      (other) =>
        other !== f &&
        relevantFactor(other) &&
        couldApply(other, answers) &&
        (other.kind === "multiplier" ||
          bindings(other).some((b) => b.id === questionId) ||
          (f.kind === "per-unit" && other.kind === "per-unit" && other.answerId === f.answerId)),
    );
  return conflicts ? undefined : rule;
}

/** Units aren't a schema field. Recognize explicit unit text only, and omit
 * missing/conflicting units instead of interpreting business-specific titles. */
export function quantityUnit(
  model: ExecutableDiscoveryModel,
  questionId: string,
  lang: string,
  form: "quantity" | "rate" = "quantity",
) {
  const q = model.scenario.steps.flatMap((s) => s.questions).find((q) => q.id === questionId);
  const units = new Set<string>();
  for (const title of Object.values(q?.title ?? {})) {
    // An explicit unsupported area/volume unit also makes mixed unit labels unsafe.
    if (
      [...title.matchAll(/\b[a-z]+(?:[²³]|[23]\b)/gi)].some(([unit]) => !/^m(?:²|2)$/i.test(unit))
    )
      units.add("unsupported");
    const withoutAreaUnits = title.replace(
      /(?:^|[^a-z])(?:m²|m2|sqm)(?=$|[^a-z0-9])|square\s+met(?:er|re)s?|מ["״']ר|מטר(?:ים)?\s+רבוע(?:ים)?/gi,
      () => {
        units.add("squareMeter");
        return " ";
      },
    );
    if (/\bmet(?:er|re)s?\b|(?:^|[\s(])(?:m|מטר(?:ים)?)(?=$|[\s)])/i.test(withoutAreaUnits))
      units.add("meter");
    if (/\bunits?\b|(?:^|[^א-ת])יחיד(?:ה|ות)(?=$|[^א-ת])/i.test(title)) units.add("count");
  }
  if (units.size !== 1 || units.has("unsupported")) return undefined;
  const translations: Record<string, string> = units.has("squareMeter")
    ? journeyStrings["journey.pricing.unitSquareMeter"]
    : units.has("meter")
      ? journeyStrings["journey.pricing.unitMeter"]
      : form === "rate"
        ? journeyStrings["journey.pricing.unitCountSingular"]
        : journeyStrings["journey.pricing.unitCount"];
  return translations[lang] ?? translations["en"];
}
